import { CognitoIdentityClient, GetIdCommand, GetCredentialsForIdentityCommand } from '@aws-sdk/client-cognito-identity';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import awsmobile from '../aws-exports.js';
import { getUploadUrl } from '../services/apiService';

const identityPoolId = awsmobile.aws_cognito_identity_pool_id;
const awsRegion = awsmobile.aws_region;
const congitoUserPoolID = awsmobile.aws_user_pools_id;
const s3BucketName = awsmobile.aws_s3_bucket_name;

const config = { region: awsRegion };
const userPool = `cognito-idp.${awsRegion}.amazonaws.com/${congitoUserPoolID}`;

const cognitoClient = new CognitoIdentityClient(config);

const getId = async (idToken) => {
	const input = {
		IdentityPoolId: identityPoolId,
		...(idToken && {
			Logins: {
				[userPool]: idToken,
			},
		}),
	};
	const command = new GetIdCommand(input);
	const response = await cognitoClient.send(command);
	console.log(response);
	localStorage.setItem('identityId', response?.IdentityId);
	return response?.IdentityId;
};

const getIAMCreds = async (idToken) => {
	const identityId = localStorage.getItem('identityId') || (await getId(idToken || false));

	const session = JSON.parse(sessionStorage.getItem(identityId));
	if (session && session.exp > Date.now()) {
		console.log('IAM Creds  still valid');
		return session.credentials;
	}
	console.log('downloading IAM creds');
	const input = {
		IdentityId: identityId,
		...(idToken && {
			Logins: {
				[userPool]: idToken,
			},
		}),
	};
	const command = new GetCredentialsForIdentityCommand(input);
	const response = await cognitoClient.send(command);
	console.log('downloaded IAM creds');
	console.log(response);

	const credentials = {
		accessKeyId: response.Credentials.AccessKeyId,
		secretAccessKey: response.Credentials.SecretKey,
		sessionToken: response.Credentials.SessionToken,
	};

	sessionStorage.setItem(
		identityId,
		JSON.stringify({
			credentials,
			exp: new Date(response?.Credentials?.Expiration).getTime(),
		})
	);
	return credentials;
};

const getFileFromS3 = async (key, idToken) => {
	console.log('Downloading ', key);
	const config = { region: awsRegion };
	config.credentials = await getIAMCreds(idToken);
	const s3Client = new S3Client(config);
	const input = {
		Bucket: s3BucketName,
		Key: key,
	};
	const command = new GetObjectCommand(input);
	const response = await s3Client.send(command);
	console.log(response);

	const stream = response.Body;

	const chunks = [];
	for await (const chunk of stream) {
		chunks.push(chunk);
	}
	const blob = new Blob(chunks);

	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = key;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
};

// Uploads a BOQ file with a presigned POST from the backend. The backend decides the
// S3 location from the signed-in user's company, so no AWS keys are needed here.
const putBOQFileToS3 = async (file) => {
	try {
		const { url, fields, key, bucket } = await getUploadUrl(file.name);

		const form = new FormData();
		Object.entries(fields).forEach(([name, value]) => form.append(name, value));
		form.append('file', file); // S3 requires the file to be the last field

		const response = await fetch(url, { method: 'POST', body: form });
		if (!response.ok) {
			const text = await response.text();
			if (text.includes('EntityTooLarge')) throw new Error('File is too large (maximum 25 MB).');
			if (text.includes('AccessDenied')) throw new Error('Access denied. Please sign in again and retry.');
			throw new Error(`Upload failed (status ${response.status}).`);
		}

		console.log('BOQ upload successful:', key);
		return { success: true, fileName: key.split('/').pop(), bucket, key };
	} catch (error) {
		console.error('BOQ upload failed:', error);
		// fetch() throws a TypeError when the request is blocked (e.g. missing S3 CORS rules) or offline.
		if (error instanceof TypeError) {
			throw new Error('Could not reach the upload server. Please check your connection or contact support.');
		}
		throw error;
	}
};

export { getId, getIAMCreds, getFileFromS3, putBOQFileToS3 };
// ------------------------------
// Chat History API (DynamoDB via API Gateway)
// ------------------------------
// TODO: replace with your deployed API base URL
const CHAT_API_BASE_URL = 'https://tw9y9zwnj0.execute-api.eu-west-2.amazonaws.com/prod';

// Save one chat turn (message + response)
export async function saveChatHistory({ userId, chatId, title, message, response }) {
	const res = await fetch(`${CHAT_API_BASE_URL}/chat`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ userId, chatId, title, message, response })
	});
	if (!res.ok) throw new Error(`saveChatHistory failed: ${res.status}`);
	return res.json();
}

// List chat titles for a user
export async function getChatHistoryList(userId) {
	const res = await fetch(`${CHAT_API_BASE_URL}/chat?userId=${encodeURIComponent(userId)}`);
	if (!res.ok) throw new Error(`getChatHistoryList failed: ${res.status}`);
	return res.json();
}

// Get full chat by id
export async function getChatHistory(userId, chatId) {
	const res = await fetch(`${CHAT_API_BASE_URL}/chat/${encodeURIComponent(chatId)}?userId=${encodeURIComponent(userId)}`);
	if (!res.ok) throw new Error(`getChatHistory failed: ${res.status}`);
	return res.json();
}
